import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import apiClient from '@/api/client'

export type ThemeMode = 'dark' | 'light'
export type IconSet = 'emoji' | 'lucide'

const STORAGE_KEY = 'casino-theme-mode'
const ICON_SET_KEY = 'casino-icon-set'

export const useThemeStore = defineStore('theme', () => {
  const mode = ref<ThemeMode>(loadSavedMode())
  const iconSet = ref<IconSet>(loadSavedIconSet())
  const loading = ref(false)

  function loadSavedMode(): ThemeMode {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved === 'light' || saved === 'dark') {
        return saved
      }
    } catch {
      // localStorage unavailable (SSR, privacy mode)
    }
    return 'dark'
  }

  function loadSavedIconSet(): IconSet {
    try {
      const saved = localStorage.getItem(ICON_SET_KEY)
      if (saved === 'emoji' || saved === 'lucide') {
        return saved
      }
    } catch {
      // localStorage unavailable
    }
    return 'emoji'
  }

  function setIconSet(newSet: IconSet) {
    iconSet.value = newSet
  }

  function applyMode(themeMode: ThemeMode) {
    document.documentElement.setAttribute('data-theme', themeMode)
  }

  function toggleMode() {
    mode.value = mode.value === 'dark' ? 'light' : 'dark'
  }

  function setMode(newMode: ThemeMode) {
    mode.value = newMode
  }

  async function loadFromApi() {
    loading.value = true
    try {
      const response = await apiClient.get<{ theme: Record<string, string>; icon_set?: IconSet }>(
        '/api/settings/public'
      )
      const overrides = response.data?.theme
      if (overrides && typeof overrides === 'object') {
        applyOverrides(overrides)
      }
      const apiIconSet = response.data?.icon_set
      if (apiIconSet === 'emoji' || apiIconSet === 'lucide') {
        iconSet.value = apiIconSet
      }
    } catch {
      // API unavailable — fall back to CSS defaults
    } finally {
      loading.value = false
    }
  }

  function applyOverrides(overrides: Record<string, string>) {
    const root = document.documentElement
    for (const [key, value] of Object.entries(overrides)) {
      if (key.startsWith('--') && typeof value === 'string') {
        root.style.setProperty(key, value)
      }
    }
  }

  // Persist mode to localStorage and apply to DOM whenever it changes
  watch(mode, (newMode) => {
    applyMode(newMode)
    try {
      localStorage.setItem(STORAGE_KEY, newMode)
    } catch {
      // localStorage unavailable
    }
  })

  // Persist icon set to localStorage whenever it changes
  watch(iconSet, (newSet) => {
    try {
      localStorage.setItem(ICON_SET_KEY, newSet)
    } catch {
      // localStorage unavailable
    }
  })

  // Apply on store creation
  applyMode(mode.value)

  return {
    mode,
    iconSet,
    loading,
    toggleMode,
    setMode,
    setIconSet,
    loadFromApi,
    applyOverrides,
  }
})
